import type { RosterEntryLive } from '@discovr/types';
import { resolvePlatform as _resolvePlatform } from '@discovr/types';
import { formatLocalDateTime } from '../../lib/datetime';
export { resolvePlatform } from '@discovr/types';

// ─── Fuzzy search ────────────────────────────────────────────────────────────

/**
 * Fuzzy match: returns true if all characters of `query` appear in order
 * in `text` (subsequence match). Falls back to substring match first as a
 * fast path. Both are case-insensitive.
 */
export function fuzzyMatch(text: string, query: string): boolean {
  const t = text.toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return true;
  if (t.includes(q)) return true;
  let qi = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

// ─── Admin-only pagination constant ──────────────────────────────────────────

export const CREATOR_PAGE_SIZE = 12;

// ─── Content kind ─────────────────────────────────────────────────────────────

/** Maps BE content_kind to the platform-native display label. */
export function contentKindLabel(kind?: string): string {
  switch (kind) {
    case 'reel':  return 'Reel';
    case 'post':  return 'Post';
    case 'short': return 'Short';
    case 'video': return 'Video';
    default:      return 'Content';
  }
}

/** Tailwind aspect-ratio class for the video popup content area. */
export function contentKindAspect(kind?: string): string {
  switch (kind) {
    case 'reel':
    case 'short': return 'aspect-[9/16]';
    case 'post':  return 'aspect-square';
    default:      return 'aspect-video';
  }
}

// ─── Formatters ───────────────────────────────────────────────────────────────

export function fmtDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** e.g. 01 Apr 2026, 17:47 */
export function fmtDateTime(iso?: string) {
  if (!iso) return '—';
  return formatLocalDateTime(iso);
}

export function buildIgUrl(v?: string) {
  if (!v) return undefined;
  if (v.startsWith('http')) return v;
  return `https://www.instagram.com/${v.replace(/^@/, '')}`;
}

export function fmtHandle(v?: string) {
  if (!v || v.trim().toLowerCase() === 'unknown') return '—';
  const s = v.trim();
  if (s.startsWith('http')) {
    try {
      const url = new URL(s);
      const path = url.pathname.replace(/\/$/, '').split('/').pop() || '';
      return `@${path.replace(/^@/, '')}`;
    } catch {
      return s;
    }
  }
  return `@${s.replace(/^@/, '')}`;
}


/**
 * Labels for the three deliverable tiers (lowest → highest cost), keyed by the
 * persisted commercial-breakdown fields `integrated`, `dedicated`, `shorts`.
 *
 * Instagram campaigns: Stories (lowest) → Posts (medium) → Reels (highest).
 * YouTube campaigns: Integrated Video → Dedicated Video → Shorts.
 */
export type DeliverableKey = 'integrated' | 'dedicated' | 'shorts';
export function getDeliverableLabels(
  platformOrCampaign: string | { platform?: string } | null | undefined,
): Record<DeliverableKey, string> {
  const raw = typeof platformOrCampaign === 'string' ? platformOrCampaign : platformOrCampaign?.platform;
  return _resolvePlatform(raw) === 'youtube'
    ? { integrated: 'Integrated Video', dedicated: 'Dedicated Video', shorts: 'Shorts' }
    : { integrated: 'Posts', dedicated: 'Reels', shorts: 'Stories' };
}

export function getDeliverableAbbrevs(
  platformOrCampaign: string | { platform?: string } | null | undefined,
): Record<DeliverableKey, string> {
  const raw = typeof platformOrCampaign === 'string' ? platformOrCampaign : platformOrCampaign?.platform;
  return _resolvePlatform(raw) === 'youtube'
    ? { integrated: 'I', dedicated: 'D', shorts: 'S' }
    : { integrated: 'P', dedicated: 'R', shorts: 'St' };
}

export function getCreatorAvatarUrl(creator: { [key: string]: unknown } | null | undefined): string | undefined {
  if (!creator) return undefined;
  const keys = [
    'youtube_channel_avatar',
    'profile_pic',
    'profile_image',
    'avatar_url',
    'photo_url',
    'image_url',
  ];
  for (const key of keys) {
    const val = creator[key];
    if (typeof val === 'string' && val.trim()) return val;
  }
  return undefined;
}

// ─── Shared workflow guards (neutral names) ───────────────────────────────────

/**
 * Whether an admin may submit (or re-submit) a script for this creator.
 *   script_pending → can submit (first upload)
 *   script_review  → can re-submit (replace pending version)
 *   anything later → row has left the Scripts tab; default true
 */
export function canSubmitScriptRow(c: { status: string }) {
  return c.status !== 'script_approved';
}

export function canSubmitContentRow(c: { status: string }) {
  // Allowed only on `content_pending` (script approved, content not yet
  // submitted). Later states are no-ops; earlier means script not approved.
  return c.status === 'content_pending';
}

// ─── Live analytics map — built from /roster/live-content ────────────────────

/**
 * Keyed by roster entry id (views, likes).
 * Reads live_video.statistics from the /roster/live-content endpoint.
 * Rows without a live_video (status=live_pending) are skipped.
 */
export function buildLiveAnalyticsMap(
  entries: ReadonlyArray<RosterEntryLive>,
): Record<string, Record<string, unknown>> {
  const result: Record<string, Record<string, unknown>> = {};
  for (const entry of entries) {
    const stats = entry.live_video?.statistics;
    if (!stats || !stats.view_count) continue;
    result[entry.id] = {
      views: stats.view_count,
      likes: stats.like_count,
      creator_id: entry.id,
      creator_name: entry.creator?.name ?? entry.platform.username ?? '',
      live_url: entry.live_video?.url ?? '',
    };
  }
  return result;
}

// ─── Content status mapping ───────────────────────────────────────────────────

export function mapCreatorStatusToContentStatus(s: string) {
  const x = s.toLowerCase();
  if (x === 'content_live' || x === 'live') return 'content_live';
  if (x === 'content_approved') return 'content_approved';
  return x;
}
